import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import CTAButtons from "@/components/shared/CTAButtons";
import heroBg from "@/assets/hero-bg.webp";

const YOUTUBE_ID = "UGGazN8nBH8";

export default function HeroSection() {
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    const timer = setTimeout(() => setVideoReady(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
      {/* Static fallback image */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt=""
          width={1920}
          height={1080}
          className="w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
      </div>

      {/* YouTube video background */}
      {videoReady && (
        <div
          className="absolute inset-0 transition-opacity duration-1000"
          aria-hidden="true"
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_ID}?autoplay=1&mute=1&loop=1&playlist=${YOUTUBE_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1`}
            title=""
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300%] h-[300%] md:w-[120%] md:h-[120%] pointer-events-none"
            allow="autoplay; encrypted-media"
            tabIndex={-1}
          />
        </div>
      )}

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/0" />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center md:text-left md:max-w-3xl md:mr-auto">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-muted-foreground text-lg mb-2 font-body"
        >
          Welcome to
        </motion.p>

        <motion.h1
          id="hero-heading"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="text-5xl md:text-7xl font-display font-bold uppercase leading-tight tracking-tight text-foreground"
        >
          Shine Bright Auto
          <br />
          Solutions
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-6 font-display text-base md:text-lg tracking-[0.15em] uppercase text-muted-foreground"
        >
          Protect It · Correct It · Shade It · Shine It
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-10"
        >
          <CTAButtons className="md:justify-start justify-center" />
        </motion.div>
      </div>
    </section>
  );
}
